// The top bar's breadcrumb, `<parent> / <page>` (BRK-S269; docs/navigation.md).
// Nav, 2026-09-28: "<listview|reportview|dashboard>/<link-name|link-title|report
// title etc>", in the navbar, as the one standard for a page's navigation.
//
// - The parent is the list, report or dashboard the page sits under: the
//   sidebar destination whose path the page's path extends. So a record, a
//   new-item page or a flow finds its way back with nothing declared.
// - The page is named by its sidebar label if it is a destination itself,
//   else by the name it gives (a record's title, through usePageTrailName),
//   else by its route's label in usePageTitle's table. That table is generic
//   where a name is PII, since it also titles the browser tab.
import { PRODUCT_LINE_NAV, type NavItemConfig } from "@/platform/productLine";
import { routeLabel } from "@/lib/usePageTitle";

/** One link in the trail. */
export interface TrailLink {
  label: string;
  to: string;
}

export interface PageTrail {
  /** Further up than the parent, outermost first — only where the page names
   *  its own ancestry (BRK-S399). Shown from md; a phone shows the parent. */
  ancestors?: TrailLink[];
  /** The list, report or dashboard the page sits under, as a link back. */
  parent?: TrailLink;
  /** The page's own name. */
  current: string;
}

/** Every sidebar destination on both platforms. Their paths never overlap
 *  (Surety's all sit under /surety), so one list serves both. */
const DESTINATIONS = Object.values(PRODUCT_LINE_NAV)
  .flat()
  .filter((item): item is NavItemConfig & { to: string } => Boolean(item.to));

/**
 * The trail for a path, or null where nothing names the page (the 404).
 *
 * `via` is the page's OWN ancestry, outermost first, for a record that sits
 * under another record rather than under the list it was opened from: a bond
 * submission belongs to its account, "Accounts / ABC Construction Services /
 * SB-40740" (BRK-S399). The nearest is the parent, the way back a phone shows.
 */
export function pageTrail(
  pathname: string,
  name?: string,
  via?: readonly TrailLink[],
): PageTrail | null {
  const own = DESTINATIONS.find((d) => d.to === pathname);
  if (own) return { current: own.label };

  const current = name?.trim() || routeLabel(pathname);
  if (!current) return null;

  if (via && via.length > 0) {
    const ancestors = via.slice(0, -1);
    const parent = via[via.length - 1];
    return ancestors.length > 0
      ? { ancestors: [...ancestors], parent, current }
      : { parent, current };
  }

  // The deepest destination the path runs under.
  const parent = DESTINATIONS.filter((d) =>
    pathname.startsWith(`${d.to}/`),
  ).sort((a, b) => b.to.length - a.to.length)[0];
  return parent
    ? { parent: { label: parent.label, to: parent.to }, current }
    : { current };
}
